import React from "react";
import ReactDOM from "react-dom/client";
import "bootstrap/dist/css/bootstrap.min.css";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { msalConfig, ssoSilentRequest } from "./msalconfig"; // Adjusted path for clarity
import { NavigationProvider } from "./contexts/NavigationContext";
import { MicrogridProvider } from "./features/microgrids/MicrogridContext";
import { SimulationDataProvider } from "./contexts/SimulationDataContext";
import { ExportedDataProvider } from "./contexts/ExportedDataContext";
import "./styles/main.bundle.css";

const msalInstance = new PublicClientApplication(msalConfig);

const rootEl = document.getElementById("root");
const root = rootEl ? ReactDOM.createRoot(rootEl) : null;

const renderFatal = (title, err) => {
  if (!root) return;
  const details =
    err?.stack ||
    err?.message ||
    (typeof err === "string" ? err : JSON.stringify(err, null, 2));

  root.render(
    <div style={{ padding: 24, fontFamily: "Arial, sans-serif" }}>
      <h2 style={{ margin: 0 }}>{title}</h2>
      <p style={{ marginTop: 12, marginBottom: 0 }}>
        Open DevTools console for more details.
      </p>
      {details ? (
        <pre
          style={{
            marginTop: 12,
            padding: 12,
            background: "#f6f8fa",
            borderRadius: 8,
            overflowX: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {String(details)}
        </pre>
      ) : null}
    </div>,
  );
};

// Surface runtime failures that otherwise look like a "blank page".
// (React render errors, uncaught exceptions, unhandled promise rejections, etc.)
window.addEventListener("error", (event) => {
  // eslint-disable-next-line no-console
  console.error("Uncaught error:", event?.error || event);
  renderFatal("App crashed (uncaught error)", event?.error || event?.message);
});
window.addEventListener("unhandledrejection", (event) => {
  // eslint-disable-next-line no-console
  console.error("Unhandled promise rejection:", event?.reason || event);
  renderFatal("App crashed (unhandled promise rejection)", event?.reason);
});

// Always render *something* immediately so we never present a blank page
// while waiting on async startup work (MSAL init, silent SSO, etc).
if (root) {
  root.render(
    <div style={{ padding: 24, fontFamily: "Arial, sans-serif" }}>
      <h2 style={{ margin: 0 }}>Loading…</h2>
    </div>,
  );
}

const initializeApp = async () => {
  try {
    // Explicitly initialize the PublicClientApplication
    await msalInstance.initialize();

    // Ensure we have an active account and warm up SSO silently to avoid future prompts
    const cachedAccounts = msalInstance.getAllAccounts();
    if (cachedAccounts.length > 0) {
      msalInstance.setActiveAccount(cachedAccounts[0]);
    } else {
      // Try a best-effort silent SSO using a stored login hint (if available)
      const storedEmail = window.sessionStorage.getItem("email") || window.localStorage.getItem("email");
      if (storedEmail) {
        try {
          await msalInstance.ssoSilent({
            ...ssoSilentRequest,
            loginHint: storedEmail,
          });
        } catch {
          // Ignore silent SSO failures; later acquireTokenSilent will still be attempted
        }
      }
    }

    if (!root) return;

    // Note: StrictMode removed to prevent double-mounting in development.
    // This reduces duplicate API calls. Re-enable for debugging side effects.
    root.render(
      <MsalProvider instance={msalInstance}>
        <NavigationProvider>
          <MicrogridProvider>
            <SimulationDataProvider>
              <ExportedDataProvider>
                <App />
              </ExportedDataProvider>
            </SimulationDataProvider>
          </MicrogridProvider>
        </NavigationProvider>
      </MsalProvider>,
    );

    reportWebVitals();
  } catch (error) {
    // If MSAL initialization fails, the app would otherwise render nothing.
    // Render a minimal error UI so the failure is visible.
    // eslint-disable-next-line no-console
    // eslint-disable-next-line no-console
    console.error("Failed to initialize MSAL instance:", error);
    renderFatal("App failed to start (MSAL init)", error);
  }
}

// Call the initialization function
initializeApp();
